import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './AdminManagement.scss'
import { HorizontalLoader, Pagination } from '../../../../Components'
import adminManagementService, {
  adminFullName,
  adminStatusLabel,
  platformLabel,
} from '../../../../Services/adminManagementService'
import {
  AdminUser,
  CreateAdminUserRequest,
  Platform,
  PlatformEnvironment,
  Role,
} from '../../../../Models/AdminManagement'
import AddAdminModal from './Modals/AddAdminModal'
import useCurrentAdmin from '../../../../Hooks/Queries/AdminManagement/useCurrentAdmin'
import Swal from 'sweetalert2'

type StatusFilter = 'ALL' | 'ACTIVE' | 'DISABLED'

const PAGE_SIZE = 15

const AdminManagement: React.FC = () => {
  const navigate = useNavigate()
  const { admin: currentAdmin, canCreateAdmin, canManageAdmins } = useCurrentAdmin()

  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)

  const [roles, setRoles] = useState<Role[]>([])
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [environments, setEnvironments] = useState<PlatformEnvironment[]>([])

  // Filtering is server-side
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), 400)
    return () => clearTimeout(timer)
  }, [search])

  // Any change to the query resets to the first page.
  useEffect(() => {
    setPage(1)
  }, [appliedSearch, statusFilter])

  const fetchAdmins = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const { users, total: reportedTotal } = await adminManagementService.getUsers({
        page,
        limit: PAGE_SIZE,
        search: appliedSearch || undefined,
        disabled: statusFilter === 'ALL' ? undefined : statusFilter === 'DISABLED',
      })
      setAdmins(users)
      setTotal(reportedTotal)
    } catch (e) {
      setAdmins([])
      setTotal(null)
      setLoadError((e as Error)?.message || 'Failed to load administrators.')
    } finally {
      setLoading(false)
    }
  }, [page, appliedSearch, statusFilter])

  useEffect(() => {
    fetchAdmins()
  }, [fetchAdmins])

  useEffect(() => {
    adminManagementService
      .getMetadata()
      .then((meta) => {
        setRoles(meta.roles ?? [])
        setPlatforms(meta.platforms ?? [])
        setEnvironments(meta.environments ?? [])
      })
      .catch((e) => {
        console.error('Error fetching admin metadata', e)
      })
  }, [])

  const handleCreateAdmin = async (payload: CreateAdminUserRequest) => {
    // Errors propagate to the modal, which shows them inline.
    const created = await adminManagementService.createUser(payload)
    await Swal.fire({
      icon: 'success',
      title: 'Administrator Created',
      text: `${adminFullName(created)} has been added.`,
      confirmButtonColor: '#e873a8',
    })
    fetchAdmins()
  }

  const handleToggleStatus = (target: AdminUser) => {
    const disabling = !target.isDisabled
    const name = adminFullName(target)

    Swal.fire({
      title: disabling ? 'Disable Admin?' : 'Enable Admin?',
      text: disabling
        ? `${name} will immediately lose access to every platform.`
        : `${name} will regain the access listed on their profile.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e873a8',
      cancelButtonColor: '#82858a',
      confirmButtonText: disabling ? 'Yes, Disable' : 'Yes, Enable',
    }).then(async (result) => {
      if (!result.isConfirmed) return

      try {
        if (disabling) {
          await adminManagementService.disableUser(target.id)
        } else {
          await adminManagementService.enableUser(target.id)
        }
        Swal.fire({
          icon: 'success',
          title: disabling ? 'Disabled' : 'Enabled',
          text: `${name} is now ${disabling ? 'disabled' : 'active'}.`,
          confirmButtonColor: '#e873a8',
        })
        fetchAdmins()
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: (e as Error)?.message || 'Failed to update status.',
        })
      }
    })
  }

  const resetFilters = () => {
    setSearch('')
    setStatusFilter('ALL')
    setPage(1)
  }

  // The API's `meta` shape is unconfirmed, so fall back to prev/next paging when
  // no total is reported rather than rendering a wrong page count.
  const knownTotal = total !== null && total >= 0
  const canGoNext = knownTotal ? page * PAGE_SIZE < total : admins.length === PAGE_SIZE

  return (
    <div className='AdminManagement'>
      <div className='container mt-4'>
        <div className='container'>
          <div className='card border-0'>
            <div className='card-header border-0 px-0'>
              <div className='row'>
                <div className='col-6 col-md-6 my-auto'>
                  <div className='title'>
                    {knownTotal ? `${total} total admins` : 'Administrators'}
                  </div>
                </div>
                <div className='col-6 col-md-6 text-end'>
                  {canCreateAdmin && (
                    <button className='btn btn-add-company' onClick={() => setShowAddModal(true)}>
                      <svg
                        width='17'
                        height='17'
                        viewBox='0 0 20 20'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M13.5 9.5625H4.5C4.1925 9.5625 3.9375 9.3075 3.9375 9C3.9375 8.6925 4.1925 8.4375 4.5 8.4375H13.5C13.8075 8.4375 14.0625 8.6925 14.0625 9C14.0625 9.3075 13.8075 9.5625 13.5 9.5625Z'
                          fill='white'
                        />
                        <path
                          d='M9 14.0625C8.6925 14.0625 8.4375 13.8075 8.4375 13.5V4.5C8.4375 4.1925 8.6925 3.9375 9 3.9375C9.3075 3.9375 9.5625 4.1925 9.5625 4.5V13.5C9.5625 13.8075 9.3075 14.0625 9 14.0625Z'
                          fill='white'
                        />
                      </svg>
                      Add Admin
                    </button>
                  )}
                </div>
              </div>

              <div className='row mt-2'>
                <div className='col-12 col-md-10'>
                  <div className='search-field'>
                    <span>
                      <svg
                        width='17'
                        height='17'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z'
                          stroke='#868484'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M22 22L20 20'
                          stroke='#868484'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Search admin by name or email'
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className='col-6 col-md-2 dropdown'>
                  <button
                    className='btn btn-filter dropdown-toggle'
                    data-bs-toggle='dropdown'
                    aria-expanded='false'
                  >
                    <svg
                      width='17'
                      height='17'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M10.94 22.65C10.46 22.65 9.99002 22.53 9.55002 22.29C8.67002 21.8 8.14002 20.91 8.14002 19.91V14.61C8.14002 14.11 7.81002 13.36 7.50002 12.98L3.76002 9.02001C3.13002 8.39001 2.65002 7.31001 2.65002 6.50001V4.20001C2.65002 2.60001 3.86002 1.35001 5.40002 1.35001H18.6C20.12 1.35001 21.35 2.58001 21.35 4.10001V6.30001C21.35 7.35001 20.72 8.54001 20.13 9.13001L15.8 12.96C15.38 13.31 15.05 14.08 15.05 14.7V19C15.05 19.89 14.49 20.92 13.79 21.34L12.41 22.23C11.96 22.51 11.45 22.65 10.94 22.65ZM5.40002 2.85001C4.70002 2.85001 4.15002 3.44001 4.15002 4.20001V6.50001C4.15002 6.87001 4.45002 7.59001 4.83002 7.97001L8.64002 11.98C9.15002 12.61 9.65002 13.66 9.65002 14.6V19.9C9.65002 20.55 10.1 20.87 10.29 20.97C10.71 21.2 11.22 21.2 11.61 20.96L13 20.07C13.28 19.9 13.56 19.36 13.56 19V14.7C13.56 13.63 14.08 12.45 14.83 11.82L19.11 8.03001C19.45 7.69001 19.86 6.88001 19.86 6.29001V4.10001C19.86 3.41001 19.3 2.85001 18.61 2.85001H5.40002Z'
                        fill='#868484'
                      />
                    </svg>
                    Filters
                  </button>
                  <div className='dropdown-menu dropdown-menu-end border-0 shadow filter-dropdown'>
                    <div className='dropdown-item'>
                      <div className='form-group mb-3'>
                        <label>Status</label>
                        <select
                          className='form-control'
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                        >
                          <option value='ALL'>All</option>
                          <option value='ACTIVE'>Active</option>
                          <option value='DISABLED'>Disabled</option>
                        </select>
                      </div>

                      <div className='row mt-3'>
                        <div className='col-12'>
                          <button type='button' className='btn btn-reset' onClick={resetFilters}>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card-body'>
              {loading && <HorizontalLoader />}

              {loadError && (
                <div className='alert alert-danger d-flex justify-content-between align-items-center'>
                  <span>{loadError}</span>
                  <button type='button' className='btn btn-sm btn-reset' onClick={fetchAdmins}>
                    Retry
                  </button>
                </div>
              )}

              <div className='table-responsive'>
                <table className='table'>
                  <thead>
                    <tr>
                      <td>S/N</td>
                      <td>Name</td>
                      <td>Email</td>
                      <td>Roles</td>
                      <td>Platform Access</td>
                      <td>Status</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length > 0 ? (
                      admins.map((adm, index) => {
                        const openDetails = () => navigate(`/admin-management/${adm.id}`)
                        const isSelf = currentAdmin?.id === adm.id
                        const grantedPlatforms = Array.from(
                          new Set((adm.platforms ?? []).map((g) => g.platform)),
                        )

                        return (
                          <tr key={adm.id}>
                            <td onClick={openDetails}>{(page - 1) * PAGE_SIZE + index + 1}</td>
                            <td onClick={openDetails}>
                              {adminFullName(adm)}
                              {isSelf && <span className='self-tag'>you</span>}
                            </td>
                            <td onClick={openDetails}>{adm.email}</td>
                            <td>
                              {adm.roles?.length ? (
                                adm.roles.map((role) => (
                                  <span key={role} className='role-tag'>
                                    {role}
                                  </span>
                                ))
                              ) : (
                                <span className='text-muted'>—</span>
                              )}
                            </td>
                            <td>
                              {grantedPlatforms.length ? (
                                grantedPlatforms.map((p) => (
                                  <span key={p} className='role-tag'>
                                    {platformLabel(p)}
                                  </span>
                                ))
                              ) : (
                                <span className='text-muted'>—</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={
                                  adm.isDisabled ? 'accont-status-inactive' : 'accont-status'
                                }
                              >
                                {adminStatusLabel(adm)}
                              </span>
                            </td>
                            <td className='dropdown option-dropdown'>
                              <button
                                className='btn border-0 dropdown-toggle'
                                data-bs-toggle='dropdown'
                                aria-expanded='false'
                                data-bs-popper-config='{"strategy":"fixed"}'
                              >
                                <svg
                                  width='18'
                                  height='18'
                                  viewBox='0 0 18 18'
                                  fill='none'
                                  xmlns='http://www.w3.org/2000/svg'
                                >
                                  <path
                                    d='M7.49991 8.4705C7.49991 7.72933 8.0955 7.14697 8.82344 7.14697C9.55138 7.14697 10.147 7.74256 10.147 8.4705C10.147 9.19844 9.55138 9.79403 8.82344 9.79403C8.0955 9.79403 7.49991 9.21168 7.49991 8.4705Z'
                                    fill='#707070'
                                  />
                                  <path
                                    d='M7.49991 14.1175C7.49991 13.3763 8.0955 12.7939 8.82344 12.7939C9.55138 12.7939 10.147 13.3895 10.147 14.1175C10.147 14.8454 9.55138 15.441 8.82344 15.441C8.0955 15.441 7.49991 14.8587 7.49991 14.1175Z'
                                    fill='#707070'
                                  />
                                  <path
                                    d='M7.49991 2.82353C7.49991 2.08235 8.0955 1.5 8.82344 1.5C9.55138 1.5 10.147 2.09559 10.147 2.82353C10.147 3.55147 9.55138 4.14706 8.82344 4.14706C8.0955 4.14706 7.49991 3.56471 7.49991 2.82353Z'
                                    fill='#707070'
                                  />
                                </svg>
                              </button>
                              <ul className='dropdown-menu dropdown-menu-end export-dropdown border-0 shadow'>
                                <li className='dropdown-item' onClick={openDetails}>
                                  View Details
                                </li>
                                {canManageAdmins && !isSelf && (
                                  <li
                                    className={
                                      adm.isDisabled ? 'dropdown-item' : 'dropdown-item text-danger'
                                    }
                                    onClick={() => handleToggleStatus(adm)}
                                  >
                                    {adm.isDisabled ? 'Enable' : 'Disable'}
                                  </li>
                                )}
                              </ul>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className='text-center py-4 text-muted'>
                          {loading ? 'Loading…' : 'No administrators found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {knownTotal && total > PAGE_SIZE && (
              <div className='pagination w-100 d-flex justify-content-center mt-3'>
                <Pagination
                  className='pagination-bar ps-0'
                  currentPage={page}
                  totalCount={total}
                  pageSize={PAGE_SIZE}
                  onPageChange={(next: number) => setPage(next)}
                />
              </div>
            )}

            {!knownTotal && (page > 1 || canGoNext) && (
              <div className='w-100 d-flex justify-content-center align-items-center gap-3 mt-3'>
                <button
                  type='button'
                  className='btn btn-reset'
                  disabled={page === 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className='text-muted small'>Page {page}</span>
                <button
                  type='button'
                  className='btn btn-reset'
                  disabled={!canGoNext || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {canCreateAdmin && (
        <AddAdminModal
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreateAdmin}
          roles={roles}
          platforms={platforms}
          environments={environments}
        />
      )}
    </div>
  )
}

export default AdminManagement
